import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react'
import { FormProvider, useForm } from 'react-hook-form'
import { Fragment, FunctionComponent, useState } from 'react'
import PatientForm from './PatientForm'
import { Patient } from '../../types'
import { useCreatePatientMutation } from '../../services/patients'
import { FetchBaseQueryErrorResult } from '../../services/baseQuery'
import { ServerError } from '../../types/result'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import { LimitedPrescriberProfile } from '../../features/users/UsersSlice'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import {
  resetDvaValidationResult,
  resetIhiValidationResult,
  resetMedicareValidationResult,
} from './PatientFormSlice'
import { formatIHIValidate, formatIHIRecord } from './utils'
import { useHasEntitlement } from '../OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types/organization-entitlements'
import { CreatePatientRequest, emptyPatient } from '../../utils/patient/patientPayload'
import {
  getMissingPrescriberMessage,
  getPatientAccessScope,
} from '../../features/patient/patientAccessScope'
interface NewPatientModalProps {
  onAdd?: (patient: Patient) => void
  children: FunctionComponent<{
    openModal: () => void
  }>
  hidePrescriberSelection?: boolean
  selectedPrescriber: LimitedPrescriberProfile | null
  onPrescriberChange?: (id: string) => void
}

const NewPatientModal: FunctionComponent<NewPatientModalProps> = ({
  onAdd,
  children: ChildrenComponent,
  hidePrescriberSelection = false,
  selectedPrescriber,
  onPrescriberChange,
}) => {
  const toast = useToast()
  const [modalIsOpen, setModelIsOpen] = useState<boolean>(false)
  const prescriber = useSelector(({ prescriber }: RootState) => prescriber?.prescriber)
  const [createPatient, createPatientResult] = useCreatePatientMutation()

  const dispatch = useAppDispatch()
  const isSharedPatientsFeatureOn = useHasEntitlement(FeatureKey.SHARED_PATIENTS)

  const createPatientError = (createPatientResult.error as FetchBaseQueryErrorResult['error'])
    ?.data as ServerError

  const effectivePrescriber = hidePrescriberSelection ? prescriber : selectedPrescriber
  const hasValidHpii = !!effectivePrescriber?.hpii

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: emptyPatient,
  })

  const { handleSubmit, reset, setError, getValues } = methods
  const { medicareValidationResult, dvaValidationResult, ihiValidationResult } = useAppSelector(
    (state) => state.patientForm
  )

  const isMedicareAcceptable = formatIHIValidate(medicareValidationResult).isAcceptable
  const isDvaAcceptable = formatIHIValidate(dvaValidationResult).isAcceptable

  const handleCreatePatient = async (data: CreatePatientRequest) => {
    const patientScope = getPatientAccessScope({
      isSharedPatientsEnabled: isSharedPatientsFeatureOn,
      prescriberId: effectivePrescriber?.id,
    })

    if (!patientScope) {
      toast({
        title: 'Cannot create patient',
        description: getMissingPrescriberMessage('creating a patient'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!hasValidHpii) {
      toast({
        title: 'Cannot create patient',
        description: 'Selected prescriber does not have a valid HPII number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    const newIHIRecord = formatIHIRecord({
      medicareValidationResult,
      dvaValidationResult,
      ihiValidationResult,
    })
    if (!newIHIRecord) {
      const { ihi_number } = getValues()

      if (
        !ihiValidationResult.service_message?.codeField &&
        ihiValidationResult.ihi_status === '' &&
        ihi_number
      ) {
        setError('ihi_number', {
          type: 'manual',
          message: 'Please validate the IHI number',
        })
        return
      }

      toast({
        position: 'top',
        title: 'Cannot create patient',
        description: 'The patient has an invalid IHI number, Please check it and try again.',
        status: 'error',
        isClosable: true,
      })
      return
    }

    if (!isMedicareAcceptable) {
      data.medicare_no = ''
      data.medicare_irn = ''
    }
    if (!isDvaAcceptable) {
      data.dva_file_no = ''
      data.dva_card_colour = ''
    }

    const patientPayload = {
      ...data,
      ...newIHIRecord,
    }

    const result = await createPatient({
      ...patientScope,
      patient: patientPayload,
    })

    if ('data' in result) {
      onAdd?.(result.data)
      handleCloseCreation()
    }
  }

  const handleCloseCreation = () => {
    reset()
    setModelIsOpen(false)
    dispatch(resetMedicareValidationResult())
    dispatch(resetDvaValidationResult())
    dispatch(resetIhiValidationResult())
  }

  return (
    <Fragment>
      <ChildrenComponent openModal={() => setModelIsOpen(true)} />
      <Modal isOpen={modalIsOpen} onClose={handleCloseCreation} closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent
          as="form"
          width="100%"
          maxWidth="1000px"
          onSubmit={handleSubmit(handleCreatePatient)}
        >
          <ModalHeader>New Patient</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormProvider {...methods}>
              <PatientForm
                defaultPatient={emptyPatient}
                selectedPrescriber={selectedPrescriber}
                onPrescriberChange={onPrescriberChange}
                hidePrescriberSelection={hidePrescriberSelection}
              />
            </FormProvider>
          </ModalBody>
          <ModalFooter>
            {createPatientError && (
              <Alert status="error" mr={3}>
                <AlertIcon />
                <AlertDescription>{createPatientError.message}</AlertDescription>
              </Alert>
            )}
            <Button colorScheme="blue" type="submit" isLoading={createPatientResult.isLoading}>
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Fragment>
  )
}

export default NewPatientModal
